import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, Send, Mic, PhoneOff } from 'lucide-react';
import { ElevenLabsService } from './ElevenLabsService';

const API_KEY = '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';
const ELEVEN_LABS_API_KEY = 'sk_6effc1af26155b8db10f4ab1aa69d005bb747746c9821ffe';
const VOICE_ID = '21m00Tcm4TlvDq8ikWAM';

const ChatInterface = () => {
  const [isInCall, setIsInCall] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! Im your AI tutor. How can I help you learn today?'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const elevenLabs = useRef(new ElevenLabsService(ELEVEN_LABS_API_KEY));

  const speak = useCallback(async (text) => {
    try {
      setIsAISpeaking(true);
      const stream = await elevenLabs.current.streamText(text, VOICE_ID);
      await elevenLabs.current.playAudioStream(
        stream,
        () => console.log('Started speaking'),
        () => setIsAISpeaking(false),
        (error) => {
          console.error('Speech error:', error);
          setIsAISpeaking(false);
        }
      );
    } catch (error) {
      console.error('Failed to speak:', error);
      setIsAISpeaking(false);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ... Continue to Part 2
  // ... continuing from Part 1
  
  const handleSendMessage = useCallback(async (voiceInput = null) => {
    const messageToSend = voiceInput || inputMessage;
    if (!messageToSend.trim()) return;

    const newMessage = {
      role: 'user',
      content: messageToSend
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: "You are an AI tutor who helps students learn effectively. You are knowledgeable, patient, and encouraging. You break down complex topics into simpler concepts and provide clear explanations with examples when needed."
            },
            ...messages,
            newMessage
          ],
          temperature: 0.7,
          max_tokens: 150
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse
      }]);

      if (isInCall) {
        await speak(aiResponse);
      }

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again."
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, messages, isInCall, speak]);

  const toggleVoiceRecognition = useCallback(() => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      recognitionRef.current?.start();
    }
  }, [isListening]);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      
      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');

        setInputMessage(transcript);

        if (event.results[0].isFinal) {
          handleSendMessage(transcript);
          recognition.stop();
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } else {
      console.error('Speech recognition not supported');
    }
  }, [handleSendMessage]);
  if (isInCall) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col">
        {/* Fixed Header */}
        <div className="flex-none">
          <div className="mt-4 text-center">
            <div className="bg-black text-white px-4 py-1 rounded-full text-sm">
              {isListening ? 'Listening...' : isAISpeaking ? 'Speaking...' : 'Voice Ready'}
            </div>
            <div className="mt-2 text-gray-700">
              AI Tutor
            </div>
          </div>

          <div className="w-32 h-32 rounded-full overflow-hidden mx-auto my-8 bg-blue-100 flex items-center justify-center">
            <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        </div>

        {/* Scrollable Chat Area */}
        <div className="flex-1 overflow-y-auto px-4 pb-24">
          <div className="max-w-lg mx-auto space-y-4">
            {messages.map((message, index) => (
              <div key={index} className={`flex gap-4 items-start ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}>
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-blue-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                )}
                <div className={`rounded-3xl p-4 max-w-[80%] ${
                  message.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}>
                  <p>{message.content}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Fixed Bottom Controls */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-6">
          <div className="max-w-lg mx-auto flex justify-center items-center gap-6">
            <button 
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isListening ? 'bg-red-500' : 'bg-black'
              }`}
              onClick={toggleVoiceRecognition}
              disabled={isAISpeaking}
            >
              <Mic size={20} className="text-white" />
            </button>
            <button 
              className="w-12 h-12 bg-black rounded-full flex items-center justify-center"
              onClick={() => {
                if (isListening) {
                  recognitionRef.current?.stop();
                }
                setIsInCall(false);
              }}
            >
              <PhoneOff size={20} className="text-red-500" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular chat interface
  return (
    <div className="flex flex-col items-center max-w-lg mx-auto p-4">
      <div className="w-24 h-24 rounded-full overflow-hidden mb-4 bg-blue-100 flex items-center justify-center">
        <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      </div>
      
      <h1 className="text-2xl font-semibold mb-1">AI Tutor</h1>
      <p className="text-gray-600 mb-4">Your personal learning assistant</p>
      <p className="text-gray-500 text-sm mb-8">Available 24/7 to help you learn</p>

      <div className="w-full">
        <div className="max-h-[400px] overflow-y-auto mb-4">
          {messages.map((message, index) => (
            <div key={index} className="flex gap-4 items-start mb-4">
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-blue-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              )}
              <div className={`flex-1 ${message.role === 'user' ? 'ml-12' : ''}`}>
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">AI Tutor</span>
                    <span className="text-gray-500 text-sm">bot</span>
                  </div>
                )}
                <div className={`rounded-3xl p-4 ${
                  message.role === 'user' ? 'bg-blue-500 text-white ml-auto' : 'bg-gray-100'
                }`}>
                  <p>{message.content}</p>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
          {isLoading && (
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-blue-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">AI Tutor</span>
                  <span className="text-gray-500 text-sm">bot</span>
                </div>
                <div className="bg-gray-100 rounded-3xl p-4">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask anything..."
              className="w-full pl-4 pr-12 py-4 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button 
              onClick={() => handleSendMessage()}
              disabled={isLoading || !inputMessage.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black rounded-full hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Send size={16} className="text-white" />
            </button>
          </div>
          <button 
            className="p-3 hover:bg-gray-100 rounded-full"
            onClick={() => setIsInCall(true)}
          >
            <Phone size={24} className="text-gray-700" />
          </button>
        </div>

        <div className="text-center mt-4">
          <p className="text-gray-500 text-sm">This is A.I. and not a real person. Treat everything it says as fiction</p>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;